// components/PurchaseReturnReportPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import reportsAPI from '../api/reportsAPI';
import { formatDate } from '../utils/dateUtils';
import { formatNumber } from '../utils/numberUtils';

const PurchaseReturnReportPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    
    // Filter states
    const [dateRange, setDateRange] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [filters, setFilters] = useState({
        supplierId: '',
        storeId: ''
    });

    const format = num => num?.toLocaleString('en-US') || '0';

    const fetchPurchaseReturnReport = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const params = {
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                ...filters
            };
            
            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });
            
            const response = await reportsAPI.getPurchaseReturnReport(params);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching purchase return report:', err);
            setError(err.message || 'Failed to load purchase return report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchaseReturnReport();
    }, []);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDateRangeChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleGenerateReport = () => {
        fetchPurchaseReturnReport();
    };

    const handlePrintReport = () => {
        try {
            console.log('Preparing purchase return report for printing...');
            
            if (!reportData || !reportData.returnOrders || reportData.returnOrders.length === 0) {
                alert('No purchase return data available to print');
                return;
            }
    
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                alert('Please allow popups for printing');
                return;
            }
    
            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Purchase Return Report - ${new Date().toLocaleDateString()}</title>
                    <style>
                        @media print {
                            @page {
                                margin: 0.5in;
                                size: letter;
                            }
                            body {
                                font-family: Arial, sans-serif;
                                font-size: 12px;
                                line-height: 1.4;
                                color: #000;
                                margin: 0;
                                padding: 0;
                            }
                            .print-header {
                                text-align: center;
                                border-bottom: 2px solid #000;
                                padding-bottom: 10px;
                                margin-bottom: 20px;
                            }
                            .print-header h1 {
                                margin: 0;
                                font-size: 18px;
                                font-weight: bold;
                            }
                            .print-header .subtitle {
                                margin: 5px 0 0 0;
                                font-size: 14px;
                                color: #666;
                            }
                            .print-summary {
                                margin-bottom: 20px;
                            }
                            .print-table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-bottom: 20px;
                            }
                            .print-table th {
                                background: #343a40;
                                color: white;
                                padding: 8px;
                                text-align: left;
                                font-weight: bold;
                                border: 1px solid #dee2e6;
                            }
                            .print-table td {
                                padding: 8px;
                                border: 1px solid #dee2e6;
                            }
                            .print-table tr:nth-child(even) {
                                background: #f8f9fa;
                            }
                            .print-table tfoot {
                                background: #e9ecef;
                                font-weight: bold;
                            }
                            .print-totals {
                                margin-top: 20px;
                                padding: 15px;
                                background: #e9ecef;
                                border-radius: 4px;
                                border: 1px solid #dee2e6;
                            }
                            .print-totals-grid {
                                display: grid;
                                grid-template-columns: repeat(4, 1fr);
                                gap: 10px;
                                margin-top: 15px;
                            }
                            .print-total-card {
                                background: white;
                                padding: 10px;
                                border-radius: 4px;
                                border: 1px solid #dee2e6;
                                text-align: center;
                            }
                            .print-total-card h4 {
                                margin: 0 0 5px 0;
                                font-size: 12px;
                                font-weight: bold;
                                color: #495057;
                            }
                            .print-total-card p {
                                margin: 0;
                                font-size: 14px;
                                font-weight: bold;
                                color: #000;
                            }
                            .print-footer {
                                margin-top: 30px;
                                text-align: center;
                                font-size: 10px;
                                color: #666;
                                border-top: 1px solid #ccc;
                                padding-top: 10px;
                            }
                            .no-print {
                                display: none !important;
                            }
                            .reason-badge {
                                padding: 2px 8px;
                                border-radius: 12px;
                                font-size: 11px;
                                background: #fff3cd;
                                color: #856404;
                            }
                        }
                        @media screen {
                            body {
                                font-family: Arial, sans-serif;
                                padding: 20px;
                            }
                        }
                    </style>
                </head>
                <body>
                    ${generatePurchaseReturnPrintContent()}
                    <div class="print-footer">
                        Generated on ${new Date().toLocaleString()} | SJ Hardware and Supply
                    </div>
                </body>
                </html>
            `);
    
            printWindow.document.close();
    
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
            }, 500);
    
        } catch (error) {
            console.error('Print error:', error);
            alert('Error generating print report');
        }
    };
    
    const generatePurchaseReturnPrintContent = () => {
        const { returnOrders, analysis, filter } = reportData;
        
        let content = `
            <div class="print-header">
                <h1>PURCHASE RETURN REPORT</h1>
                <div class="subtitle">
                    Period: ${formatDate(filter.startDate)} to ${formatDate(filter.endDate)}
                </div>
                <div class="subtitle">
                    Generated on: ${new Date().toLocaleDateString()}
                </div>
            </div>
        `;
    
        // Summary Cards
        content += `
            <div class="print-totals">
                <h4>RETURN ANALYSIS</h4>
                <div class="print-totals-grid">
                    <div class="print-total-card">
                        <h4>Total Returns</h4>
                        <p>${analysis.totalReturns}</p>
                    </div>
                    <div class="print-total-card">
                        <h4>Total Return Value</h4>
                        <p>${formatNumber(analysis.totalReturnValue)}</p>
                    </div>
                    <div class="print-total-card">
                        <h4>Total Quantity</h4>
                        <p>${analysis.totalReturnQuantity}</p>
                    </div>
                    <div class="print-total-card">
                        <h4>Avg Return Value</h4>
                        <p>${formatNumber(analysis.totalReturnValue / analysis.totalReturns)}</p>
                    </div>
                </div>
            </div>
        `;

        // Return Reasons Breakdown
        if (analysis.byReason && Object.keys(analysis.byReason).length > 0) {
            content += `
                <div class="print-summary">
                    <h4>RETURN REASONS</h4>
                    <table class="print-table">
                        <thead>
                            <tr>
                                <th>Reason</th>
                                <th>Count</th>
                                <th>Percentage</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${Object.entries(analysis.byReason).map(([reason, count]) => `
                                <tr>
                                    <td>${reason}</td>
                                    <td>${count}</td>
                                    <td>${((count / analysis.totalReturns) * 100).toFixed(1)}%</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;
        }

        // Return Orders Table
        content += `
            <div class="print-summary">
                <h4>RETURN ORDERS</h4>
                <table class="print-table">
                    <thead>
                        <tr>
                            <th>RPO Number</th>
                            <th>Date</th>
                            <th>Supplier</th>
                            <th>Original PO</th>
                            <th>Return Reason</th>
                            <th>Items</th>
                            <th>Return Quantity</th>
                            <th>Return Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${returnOrders.map(ro => `
                            <tr>
                                <td>${ro.poNumber}</td>
                                <td>${formatDate(ro.orderDate)}</td>
                                <td>${ro.supplier?.name || 'N/A'}</td>
                                <td>${ro.originalPO?.poNumber || 'N/A'}</td>
                                <td><span class="reason-badge">${ro.returnReason || 'No reason specified'}</span></td>
                                <td>${ro.returnSummary.itemsCount}</td>
                                <td>${ro.returnSummary.totalQuantity}</td>
                                <td>${formatNumber(ro.total)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="6"><strong>TOTAL</strong></td>
                            <td><strong>${analysis.totalReturnQuantity}</strong></td>
                            <td><strong>${formatNumber(analysis.totalReturnValue)}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        `;
    
        return content;
    };

    if (loading && !reportData) {
        return (
            <Layout pageTitle="Loading Purchase Return Report...">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Purchase Return Report">
            <div className="max-w-7xl mx-auto p-6 bg-gray-50">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Purchase Return Report</h1>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => navigate('/reports')}
                            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                        >
                            Back to Reports
                        </button>
                        <button
                            onClick={handlePrintReport}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                            disabled={!reportData || reportData.returnOrders.length === 0}
                        >
                            Print Report
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {/* Filters Section */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Report Filters</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={dateRange.startDate}
                                onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={dateRange.endDate}
                                onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded"
                            />
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={handleGenerateReport}
                                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Generate Report'}
                            </button>
                        </div>
                    </div>
                </div>

                {loading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
                    </div>
                )}

                {reportData && (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                            <div className="bg-red-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-red-800">Total Returns</h3>
                                <p className="text-2xl font-bold">{reportData.analysis.totalReturns}</p>
                                <p className="text-sm text-gray-600">Return Orders</p>
                            </div>
                            <div className="bg-orange-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-orange-800">Return Value</h3>
                                <p className="text-2xl font-bold">{formatNumber(reportData.analysis.totalReturnValue)}</p>
                                <p className="text-sm text-gray-600">Total amount returned</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-purple-800">Return Quantity</h3>
                                <p className="text-2xl font-bold">{reportData.analysis.totalReturnQuantity}</p>
                                <p className="text-sm text-gray-600">Items returned</p>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-yellow-800">Avg Return</h3>
                                <p className="text-2xl font-bold">
                                    {formatNumber(reportData.analysis.totalReturnValue / reportData.analysis.totalReturns)}
                                </p>
                                <p className="text-sm text-gray-600">Per return order</p>
                            </div>
                        </div>

                        {/* Return Reasons Breakdown */}
                        {reportData.analysis.byReason && Object.keys(reportData.analysis.byReason).length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold mb-4">Return Reasons</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {Object.entries(reportData.analysis.byReason).map(([reason, count]) => (
                                        <div key={reason} className="bg-gray-50 p-4 rounded">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <div className="font-semibold text-gray-800">{reason}</div>
                                                    <div className="text-sm text-gray-600">
                                                        {((count / reportData.analysis.totalReturns) * 100).toFixed(1)}% of returns
                                                    </div>
                                                </div>
                                                <div className="text-xl font-bold text-red-600">{count}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Return Orders Table */}
                        <div>
                            <h3 className="text-lg font-semibold mb-4">Return Orders</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RPO Number</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Original PO</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return Reason</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Return Quantity</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Return Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {reportData.returnOrders.map((ro) => (
                                            <tr key={ro._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap font-medium text-red-600">{ro.poNumber}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{formatDate(ro.orderDate)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{ro.supplier?.name || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {ro.originalPO?.poNumber || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="inline-flex px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
                                                        {ro.returnReason || 'No reason specified'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">{ro.returnSummary.itemsCount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{ro.returnSummary.totalQuantity}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-red-600">
                                                    {formatNumber(ro.total)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 font-semibold">
                                        <tr>
                                            <td className="px-6 py-4 whitespace-nowrap border-t" colSpan="6">TOTAL</td>
                                            <td className="px-6 py-4 whitespace-nowrap border-t">{reportData.analysis.totalReturnQuantity}</td>
                                            <td className="px-6 py-4 whitespace-nowrap border-t text-right text-red-600">
                                                {formatNumber(reportData.analysis.totalReturnValue)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        {reportData.returnOrders.length === 0 && !loading && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No return orders found for the selected filters</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default PurchaseReturnReportPage;